import { useDispatch, useSelector } from 'store';
import {
  Autocomplete,
  TextField,
  Box,
  Avatar,
  Tooltip,
  GridLegacy,
  Popover,
  Paper,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
  Typography,
  Badge,
} from '@mui/material';
import { setActiveOrgId } from 'store/slices/apiV1/org';
import { useNavigate } from 'react-router-dom';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import {
  IconArrowsShuffle2,
  IconBeach,
  IconCornerDownRight,
  IconFilter,
  IconUser,
  IconUsersGroup,
  IconWorld,
} from '@tabler/icons-react';
import { handleOrgSwitch, setStoredOrg } from 'store/actions';
import { skipToken } from '@reduxjs/toolkit/query/react';
import Loader from 'ui-component/Loader';
import _ from 'lodash';
import { Org } from 'types/org';
import { useTheme } from '@mui/material/styles';
import { getCustomizedParts } from 'store/slices/apiV1/partData';
import { memo, useMemo, useCallback, useState } from 'react';
import { DASHBOARD_ROUTE } from 'routes/constants';
import { roundedButtonAvatarSx } from 'constants/themeConstants';
import { createSxWithoutUndefined } from 'utils/functions';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import useCustomTeamTypeNames from 'hooks/useCustomTeamTypeName';
import { useGetTeamsQuery } from 'store/slices/apiV1/properties';
import { ALL_APPS } from 'constants/appConstants';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import { RecordFilterType, setFilterState } from 'store/slices/recordFilter';

const ActiveOrgSwitcher = ({
  skipResetState,
  ignoreBlended,
}: {
  skipResetState?: boolean;
  ignoreBlended?: boolean;
}) => {
  const theme = useTheme();
  const { activeOrgId, blended, sandbox } = useSelector((state) => state.org);
  const { isLoggedIn } = useSelector((state) => state.session);
  const { drawerOpen } = useSelector((state) => state.menu);

  const { data: user, isSuccess: isGetUserSuccess } = useGetUserQuery(
    isLoggedIn ? undefined : skipToken
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tempMineAllFilter = useFeature(FeatureFlags.tempMineAllFilter).on;
  const { hasAppPermission } = useAppAccessContext();
  const propertiesAppEnabled = hasAppPermission(ALL_APPS.PROPERTIES.id);
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeNames();

  const { data: teams } = useGetTeamsQuery(
    propertiesAppEnabled && tempMineAllFilter ? undefined : skipToken
  );

  const customizedParts = useSelector(getCustomizedParts);

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
    null
  );

  const filterState = useSelector((state) => state.recordFilter);

  const handleFilterButtonClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setPopoverAnchorEl(event.currentTarget);
    },
    []
  );

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchorEl(null);
  }, []);

  const handleFilterTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newFilterType: string | null) => {
      if (newFilterType === null) return;
      const filterType = newFilterType as RecordFilterType;
      if (filterType === RecordFilterType.MyTeamsRecords && teams) {
        const allTeamIds = teams.map((team) => team.id);
        dispatch(
          setFilterState({
            filterType,
            selectedTeams: allTeamIds,
          })
        );
      } else if (filterType === RecordFilterType.AllRecords) {
        dispatch(
          setFilterState({
            filterType,
            selectedTeams: [],
          })
        );
      } else {
        dispatch(
          setFilterState({
            filterType,
            selectedTeams: filterState.selectedTeams,
          })
        );
      }
    },
    [dispatch, filterState.selectedTeams, teams]
  );

  const handleTeamToggle = useCallback(
    (teamId: string) => {
      dispatch(
        setFilterState({
          selectedTeams: filterState.selectedTeams.includes(teamId)
            ? filterState.selectedTeams.filter((id: string) => id !== teamId)
            : [...filterState.selectedTeams, teamId],
        })
      );
    },
    [dispatch, filterState.selectedTeams]
  );

  const popoverOpen = Boolean(popoverAnchorEl);

  const filterModeIcon = useMemo(() => {
    switch (filterState.filterType) {
      case RecordFilterType.MyRecords:
        return <IconUser size="0.75rem" />;
      case RecordFilterType.MyTeamsRecords:
        return <IconUsersGroup size="0.75rem" />;
      case RecordFilterType.AllRecords:
        return <IconWorld size="0.75rem" />;
      default:
        return null;
    }
  }, [filterState.filterType]);

  const orgOptions = useMemo(() => {
    if (!user || user?.orgs.length < 2) return [];
    return _.chain(user?.orgs)
      .filter((o) => !o.createdFrom)
      .sortBy('name')
      .map((o) => [
        o,
        ..._.chain(user?.orgs)
          .filter(
            (co) =>
              (typeof co.createdFrom === 'string'
                ? co.createdFrom
                : co.createdFrom?.id) === o.id
          )
          .value(),
      ])
      .flatten()
      .value();
  }, [user?.orgs]);

  const options = useMemo(() => {
    if (!user || user?.orgs.length < 2) return [];
    return _.chain(user?.orgs)
      .filter((o) => !o.createdFrom)
      .sortBy('name')
      .map((o) => [
        o,
        ..._.chain(user?.orgs)
          .filter(
            (co) =>
              (typeof co.createdFrom === 'string'
                ? co.createdFrom
                : co.createdFrom?.id) === o.id
          )
          .value(),
      ])
      .flatten()
      .value();
  }, [user]);

  const handleOptionChange = useCallback(
    (__: unknown, option: unknown) => {
      const org = option as Org | null;
      if (org) {
        dispatch(setStoredOrg(org.id));
        dispatch(
          setActiveOrgId({
            orgId: org.id,
            org,
            skipResetState,
          })
        );
        dispatch(handleOrgSwitch({ customPartIds: customizedParts }));
        if (!skipResetState) {
          navigate(DASHBOARD_ROUTE);
        }
      }
    },
    [dispatch, navigate, customizedParts, skipResetState]
  );

  const activeOrg = useMemo(
    () => _.find(user?.orgs, { id: activeOrgId }),
    [user?.orgs, activeOrgId]
  );

  if (!isLoggedIn) {
    return <></>;
  }
  if (!isGetUserSuccess) {
    return <Loader />;
  }
  if (options.length === 0) return <></>;

  return (
    <GridLegacy
      item
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
    >
      {drawerOpen && orgOptions.length > 0 && (
        <GridLegacy item xs>
          <Autocomplete
            options={options}
            getOptionLabel={(option) => (option as Org).name}
            onChange={handleOptionChange}
            autoComplete
            disabled={blended && !ignoreBlended}
            disableClearable
            value={activeOrg}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={(option as Org).id}>
                {!!(option as Org).createdFrom && (
                  <IconCornerDownRight style={{ marginRight: '6px' }} />
                )}
                {(option as Org).sandbox && (
                  <IconBeach style={{ marginRight: '6px' }} />
                )}
                {(option as Org).name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password', // disable autocomplete and autofill
                }}
                label="Active Org"
                fullWidth
              />
            )}
          />
        </GridLegacy>
      )}
      {!ignoreBlended && orgOptions.length > 0 && drawerOpen && (
        <GridLegacy item xs="auto">
          <Tooltip
            title={
              blended ? 'Disable blended view mode' : 'Enable blended view mode'
            }
          >
            <Avatar
              variant="rounded"
              sx={createSxWithoutUndefined({
                ...roundedButtonAvatarSx(theme),
                ...{
                  background: blended
                    ? theme.palette.success.main
                    : theme.palette.brand.moss,
                  color: blended
                    ? theme.palette.common.white
                    : theme.palette.brand.steel,
                  ml: 1,
                },
              })}
              onClick={() => {
                if (blended) {
                  dispatch(setActiveOrgId({ skipResetState, blended: false }));
                } else {
                  dispatch(setActiveOrgId({ skipResetState, blended: true }));
                  dispatch(handleOrgSwitch({ customPartIds: customizedParts }));
                }
                dispatch(handleOrgSwitch({ customPartIds: customizedParts }));
                if (!skipResetState) {
                  navigate(DASHBOARD_ROUTE);
                }
              }}
              color="inherit"
            >
              <IconArrowsShuffle2 stroke={1.5} size="1.3rem" />
            </Avatar>
          </Tooltip>
        </GridLegacy>
      )}
      {blended && !ignoreBlended && orgOptions.length > 0 && drawerOpen && (
        <GridLegacy item xs="auto">
          <Tooltip
            title={sandbox ? 'Disable sandbox mode' : 'Enable sandbox mode'}
          >
            <Avatar
              variant="rounded"
              sx={createSxWithoutUndefined({
                ...roundedButtonAvatarSx(theme),
                ...{
                  background: sandbox
                    ? theme.palette.success.main
                    : theme.palette.brand.moss,
                  color: sandbox
                    ? theme.palette.common.white
                    : theme.palette.brand.steel,
                  ml: 1,
                },
              })}
              onClick={() => {
                dispatch(handleOrgSwitch({ customPartIds: customizedParts }));
                if (sandbox) {
                  dispatch(
                    setActiveOrgId({
                      skipResetState,
                      blended: true,
                      sandbox: false,
                    })
                  );
                } else {
                  dispatch(
                    setActiveOrgId({
                      skipResetState,
                      blended: true,
                      sandbox: true,
                    })
                  );
                }
              }}
              color="inherit"
            >
              <IconBeach stroke={1.5} size="1.3rem" />
            </Avatar>
          </Tooltip>
        </GridLegacy>
      )}
      {tempMineAllFilter && (
        <GridLegacy item xs="auto">
          <Tooltip title="Filter Records">
            <Badge
              badgeContent={
                filterState.filterType ===
                RecordFilterType.AllRecords ? undefined : filterState.filterType ===
                  RecordFilterType.MyRecords ? (
                  <IconUser size={16} />
                ) : (
                  filterState.selectedTeams.length
                )
              }
              color="primary"
            >
              <Avatar
                variant="rounded"
                sx={createSxWithoutUndefined({
                  ...roundedButtonAvatarSx(theme),
                  ...{
                    background:
                      filterState.filterType === RecordFilterType.MyRecords ||
                      filterState.filterType ===
                        RecordFilterType.MyTeamsRecords ||
                      filterState.selectedTeams.length > 0
                        ? theme.palette.success.main
                        : theme.palette.brand.moss,
                    color:
                      filterState.filterType === RecordFilterType.MyRecords ||
                      filterState.filterType ===
                        RecordFilterType.MyTeamsRecords ||
                      filterState.selectedTeams.length > 0
                        ? theme.palette.common.white
                        : theme.palette.brand.steel,
                    ml: 1,
                    cursor: 'pointer',
                  },
                })}
                onClick={handleFilterButtonClick}
                color="inherit"
              >
                <IconFilter stroke={1.5} size="1.3rem" />
              </Avatar>
            </Badge>
          </Tooltip>
          <Popover
            open={popoverOpen}
            anchorEl={popoverAnchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Paper sx={{ p: 2, minWidth: 280 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Filter Records Globally
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  <ToggleButtonGroup
                    value={filterState.filterType}
                    onChange={handleFilterTypeChange}
                    exclusive
                    size="small"
                    orientation="vertical"
                  >
                    <ToggleButton
                      value={RecordFilterType.MyRecords}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      <IconUser size="1rem" style={{ marginRight: '4px' }} />
                      <Typography variant="body2">Mine</Typography>
                    </ToggleButton>
                    <ToggleButton
                      value={RecordFilterType.MyTeamsRecords}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      <IconUsersGroup
                        size="1rem"
                        style={{ marginRight: '4px' }}
                      />
                      <Typography variant="body2">
                        {handleReplaceWithCustomTeamName('My Teams')}
                      </Typography>
                    </ToggleButton>
                    <ToggleButton
                      value={RecordFilterType.AllRecords}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      <IconWorld size="1rem" style={{ marginRight: '4px' }} />
                      <Typography variant="body2">All</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {filterState.filterType === RecordFilterType.MyTeamsRecords &&
                    teams &&
                    teams.length > 0 && (
                      <>
                        <Stack spacing={0.5}>
                          {teams.map((team) => (
                            <FormControlLabel
                              key={team.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={filterState.selectedTeams.includes(
                                    team.id
                                  )}
                                  onChange={() => handleTeamToggle(team.id)}
                                />
                              }
                              label={team.name}
                            />
                          ))}
                        </Stack>
                      </>
                    )}
                </Stack>
              </Stack>
            </Paper>
          </Popover>
        </GridLegacy>
      )}
    </GridLegacy>
  );
};

export default memo(ActiveOrgSwitcher);
